/**
 * ReadOnlyExternalField — Read-only value display with optional external link.
 *
 * Used for parameters whose value is owned by a system outside the workflow
 * editor (e.g. a Foundry agent's `instructions` field, edited in the Foundry
 * portal). Renders the value verbatim along with a provenance tooltip and an
 * "open at source" link button when an external link builder resolves to a URL.
 *
 * Driven by `x-ms-editor: 'readonlyexternal'` plus:
 *   x-ms-editor-options: {
 *     externalLink?: { builderId: string; label?: string },
 *     provenanceLabel?: string,     // tooltip on the info glyph
 *     emptyText?: string,           // text shown when value is empty
 *   }
 */

import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Tooltip,
  Button,
} from '@fluentui/react-components';
import { Info16Regular, OpenRegular } from '@fluentui/react-icons';
import {
  getExternalLinkBuilder,
  type ExternalLinkContext,
} from '@/services/externalLinkBuilderRegistry';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  provenanceGlyph: {
    minWidth: 'auto',
    width: '20px',
    height: '20px',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground2,
    },
  },
  value: {
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    lineHeight: tokens.lineHeightBase200,
  },
  empty: {
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  linkButton: {
    marginTop: tokens.spacingVerticalXXS,
    alignSelf: 'flex-start',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

export interface ReadOnlyExternalLinkOptions {
  /** ID of a builder registered via `registerExternalLinkBuilder`. */
  builderId: string;
  /** Label for the external link button. */
  label?: string;
}

export interface ReadOnlyExternalFieldProps {
  name: string;
  label: string;
  value: string | undefined;
  description?: string | undefined;
  required?: boolean | undefined;
  /** Tooltip shown on the info glyph next to the label. */
  provenanceLabel?: string | undefined;
  /** Text shown when value is empty/undefined. */
  emptyText?: string | undefined;
  /** Optional external link configuration. */
  externalLink?: ReadOnlyExternalLinkOptions | undefined;
  /** All parameter values \u2014 needed for link builders. */
  parameterValues: Record<string, unknown>;
  /** Connection context \u2014 forwarded to link builders. */
  linkContext?: Omit<ExternalLinkContext, 'parameterValues'>;
  'data-testid'?: string | undefined;
}

export const ReadOnlyExternalField = ({
  name,
  label,
  value,
  description,
  required,
  provenanceLabel,
  emptyText,
  externalLink,
  parameterValues,
  linkContext,
  'data-testid': testId,
}: ReadOnlyExternalFieldProps) => {
  const styles = useStyles();

  const linkHref = useMemo(() => {
    if (!externalLink) return undefined;
    const builder = getExternalLinkBuilder(externalLink.builderId);
    if (!builder) return undefined;
    return builder({ parameterValues, ...linkContext });
  }, [externalLink, parameterValues, linkContext]);

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
        {provenanceLabel && (
          <Tooltip content={provenanceLabel} relationship="description" withArrow>
            <Button
              className={styles.provenanceGlyph}
              appearance="transparent"
              size="small"
              icon={<Info16Regular />}
              aria-label={`${label}: ${provenanceLabel}`}
              data-testid={`${name}-provenance`}
            />
          </Tooltip>
        )}
      </div>

      {value ? (
        <Text className={styles.value} data-testid={testId ?? `${name}-value`}>
          {value}
        </Text>
      ) : (
        <Text className={styles.empty} data-testid={testId ?? `${name}-value`}>
          {emptyText ?? 'No value'}
        </Text>
      )}

      {linkHref && (
        <Button
          appearance="transparent"
          size="small"
          icon={<OpenRegular />}
          className={styles.linkButton}
          as="a"
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${name}-external-link`}
        >
          {externalLink?.label ?? 'Edit at source'}
        </Button>
      )}

      {description && <Text className={styles.description}>{description}</Text>}
    </div>
  );
};
